'use client'

import { formatPrice } from "@/utils/util"
import { Minus, Plus, Trash2, Gift, CirclePercent, Medal } from "lucide-react"
import { Button } from "@/components/elements/Button"
import { usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { useCartItem } from "@/lib/commerce/hooks"

export default function CartCard({ product }: { product: any }) {
  const pathname = usePathname()
  const c = useCartItem(product)

  const labels = (
    <div className="flex flex-wrap items-center gap-1.5">
      {c.productData?.isDigital && (
        <span className="inline-flex items-center font-semibold bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">Дижитал</span>
      )}
      {c.showDiscountedPrice && c.discountName && (
        <span className={`inline-flex items-center font-semibold ${c.isReward ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'} text-xs px-2 py-0.5 rounded-sm`}>
          {c.isReward ? <Gift className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} /> : <CirclePercent className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />}
          <span className="line-clamp-1">{c.discountName}</span>
        </span>
      )}
      {c.loyaltyDeal && (
        <span className="inline-flex items-center font-semibold bg-violet-100 text-violet-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">
          <Medal className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />
          <span className="hidden sm:inline">Лоялти&nbsp;</span>-{c.loyaltyDeal.percent}%
        </span>
      )}
    </div>
  )

  const priceBlock = (
    <div className="text-base leading-6 font-normal text-foreground">
      {c.showDiscountedPrice ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-0.5 sm:gap-2">
          <span className={`font-medium ${c.unitPrice === 0 ? 'text-green-600' : 'text-red-600'} leading-7 tracking-[.025em]`}>{c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}</span>
          <span className="line-through text-muted-foreground text-sm leading-5 tracking-[.0125em]">{formatPrice(c.qty * c.originalPrice!)}</span>
        </div>
      ) : (
        <span className="leading-5 tracking-[.0125em]">{formatPrice(c.displayPrice)}</span>
      )}
    </div>
  )

  const qtyControls = (
    <div className="flex items-center gap-3">
      <Button onClick={c.handleRemove} disabled={c.loading} variant="ghost" size="xs" className="text-foreground bg-muted py-4!">
        <Trash2 className="h-5 w-5" strokeWidth={1.25} />
      </Button>
      {c.isReward ? (
        <div className="bg-muted flex items-center rounded-md py-1.5 px-3">
          <span className="text-sm min-w-8.5 leading-5 text-center">{c.qty}</span>
        </div>
      ) : c.isOutOfStock ? (
        <div className="bg-red-50 flex items-center rounded-md py-1.5 px-3">
          <div className="text-xs text-red-500 font-medium leading-5">Бараа дууссан</div>
        </div>
      ) : (
        <div className="bg-muted flex items-center rounded-md py-1">
          <Button onClick={c.handleRemoveOne} variant="ghost" size="xs" disabled={c.loading} className="h-6 w-6 px-4!">
            <Minus className="shrink-0 h-4 w-4" />
          </Button>
          <span className="text-sm px-2 min-w-8.5 leading-5 text-center">{c.qty}</span>
          <Button onClick={c.handleAddOne} variant="ghost" size="xs" disabled={c.loading} className="h-6 w-6 px-4!">
            <Plus className="shrink-0 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )

  const variantRow = c.showVariantName && (
    <div className="flex items-center gap-2 mt-0.5 text-sm text-muted-foreground leading-5 font-normal">
      <span>{c.variant.name}</span>
      {c.dimension && (
        <>
          <div className="w-px h-3 bg-muted-foreground"></div>
          <span>{c.dimension}</span>
        </>
      )}
    </div>
  )

  const productLink = (className: string) => (
    <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className={className}>
      <div className="relative w-24 h-24">
        <img src={c.imageUrl} alt={c.productName} className="w-full h-full rounded-md object-cover object-center" />
        {c.isReward && (
          <div className="absolute -top-2 -right-2 px-1.5 py-1 bg-orange-600 text-white rounded-full">
            <Gift className="w-4 h-4" strokeWidth={2.5} />
          </div>
        )}
      </div>
    </a>
  )

  return (
    <>
      <div className="hidden sm:flex items-center gap-3">
        {productLink("relative shrink-0")}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)}>
              <h3 className="text-base font-medium text-foreground leading-6 line-clamp-2 wrap-break-word">{c.productName}</h3>
            </a>
            {c.productData?.isDigital && (
              <span className="inline-flex items-center font-semibold bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">Дижитал</span>
            )}
            {c.showDiscountedPrice && c.discountName && (
              <span className={`inline-flex items-center font-semibold ${c.isReward ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'} text-xs px-2 py-0.5 rounded-sm`}>
                {c.isReward ? <Gift className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} /> : <CirclePercent className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />}
                <span className="line-clamp-1">{c.discountName}</span>
              </span>
            )}
            {c.loyaltyDeal && (
              <span className="inline-flex items-center font-semibold bg-violet-100 text-violet-600 text-xs px-2 py-0.5 rounded-sm whitespace-nowrap">
                <Medal className="w-3.5 h-3.5 mr-1 shrink-0" strokeWidth={2.5} />
                <span className="hidden sm:inline">Лоялти&nbsp;</span>-{c.loyaltyDeal.percent}%
              </span>
            )}
          </div>
          {variantRow}
          <div className="flex items-center justify-between mt-2">
            {priceBlock}
            {qtyControls}
          </div>
        </div>
      </div>

      <div className="sm:hidden flex flex-col gap-1">
        <div className="flex gap-3">
          {productLink("relative shrink-0")}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)}>
                  <h3 className="text-base font-medium text-foreground leading-6 line-clamp-2 wrap-break-word">{c.productName}</h3>
                </a>
                {labels}
                {c.showVariantName && (
                  <div className="flex flex-col gap-0.5 text-sm text-muted-foreground leading-5 font-normal">
                    <span>{c.variant.name}</span>
                    {c.dimension && <span>{c.dimension}</span>}
                  </div>
                )}
              </div>
              {priceBlock}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center gap-3">
          <div className="font-medium text-foreground text-base leading-5 tracking-[.0125em]">Тоо хэмжээ</div>
          <div className="flex items-center gap-1.5">{qtyControls}</div>
        </div>
      </div>
    </>
  )
}
